"use client";

// Spanish copy of components/header/header.comp.tsx (DEV-15362), slimmed per the
// batch 1 spec: logo, Widgets, Precios, Iniciar sesión, Regístrate gratis and the
// ES | EN switch. The English header's dropdowns list English widget and page
// names, so they are left out here. Reuses the English header's styles and
// always renders in its fixedPosition + lightMode state, which is how the
// widget hub hero uses it.
import { GiHamburgerMenu } from "@react-icons/all-files/gi/GiHamburgerMenu";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { ReactDimmer } from "react-dimmer";
import cn from "classnames";
import { trackEvent } from "@/lib/track";
import {
  StyledAside,
  StyledHeader,
  StyledMiniHeader,
} from "../header/header.styles";
import { esHref, hasEsTwin } from "@/i18n/es/routes";
import { EnTag, LangSwitchEs } from "./langSwitch.comp";

const NAV = [
  { label: "Widgets", en: "/widgets" },
  { label: "Plataformas", en: "/platforms" },
  { label: "Precios", en: "/pricing" },
];

// `fixedPosition` (the default) is how the widget hubs use the header. Pass
// false for the English header's default behaviour, used on the home page,
// pricing and the widget list: transparent over the hero at the top of the
// page, hidden while scrolling down, back as a floating bar when scrolling up.
// `addBackground` matches the English prop (pricing page).
export const HeaderEs = ({
  lightMode = true,
  fixedPosition = true,
  addBackground = false,
}: {
  lightMode?: boolean;
  fixedPosition?: boolean;
  addBackground?: boolean;
}) => {
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (fixedPosition) return;
    const onScroll = () => {
      const y = window.scrollY;
      setIsVisible(y < lastScrollY);
      setLastScrollY(y);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [fixedPosition, lastScrollY]);

  // Same thresholds and classes as components/header/header.comp.tsx.
  const position = fixedPosition
    ? { "fix-position": true }
    : lastScrollY < 90
    ? { "in-on-top": true }
    : { visible: isVisible };
  const logo = (
    <Image
      src={lightMode ? "/assets/cn-logo-light.svg" : "/assets/cn-logo-dark.svg"}
      alt="Logo de Common Ninja"
      width={200}
      height={30}
    />
  );

  const navLink = (n: (typeof NAV)[number], onClick?: () => void) => (
    <Link
      key={n.en}
      className="section-label"
      href={esHref(n.en)}
      onClick={onClick}
    >
      {n.label}
      {!hasEsTwin(n.en) && <EnTag />}
    </Link>
  );

  return (
    <>
      <ReactDimmer isOpen={sideNavOpen} exitDimmer={setSideNavOpen} blur={1} />
      <StyledMiniHeader
        className={cn({ "add-background": addBackground, ...position })}
      >
        <nav>
          <Link href={esHref("/")} className="logo">
            {logo}
          </Link>
          <GiHamburgerMenu
            onClick={() => setSideNavOpen(true)}
            fontSize={23}
            className="hamburger"
            aria-label="Abrir menú"
          />
        </nav>
      </StyledMiniHeader>
      <StyledAside className={cn({ opened: sideNavOpen })}>
        <div className="top">
          <Link onClick={() => setSideNavOpen(false)} href={esHref("/")}>
            {logo}
          </Link>
        </div>
        <div className="content">
          <nav>
            {NAV.map((n) => navLink(n, () => setSideNavOpen(false)))}
            <LangSwitchEs />
          </nav>
        </div>
        <div className="bottom">
          <Link href="/login" onClick={() => trackEvent("Website Login", { locale: "es" })}>
            Iniciar sesión
          </Link>
          <Link
            className="start-creating"
            href="/signup"
            title="Regístrate gratis"
            id="sidebar-main-cta"
            onClick={() => trackEvent("Start creating header", { locale: "es" })}
          >
            Regístrate gratis
          </Link>
        </div>
      </StyledAside>
      <StyledHeader
        className={cn({
          "light-mode": lightMode,
          "add-background": addBackground,
          ...position,
        })}
      >
        <nav>
          <div className="links-divider">
            <Link href={esHref("/")} className="logo">
              {logo}
            </Link>
            <div className="links">{NAV.map((n) => <div key={n.en}>{navLink(n)}</div>)}</div>
          </div>
          <div className="links-divider right-side">
            <LangSwitchEs />
            <Link
              className="login"
              href="/login"
              onClick={() => trackEvent("Website Login", { locale: "es" })}
            >
              Iniciar sesión
            </Link>
            <Link
              className="start-creating"
              href="/signup"
              title="Regístrate gratis"
              id="header-main-cta"
              onClick={() => trackEvent("Start creating header", { locale: "es" })}
            >
              Regístrate gratis
            </Link>
          </div>
        </nav>
      </StyledHeader>
    </>
  );
};
